import { useCallback, useEffect, useRef, useState } from 'react';
import { prepareSelectedSources, selectionSummary } from '../../utils/reportSourcePreparation';

export default function useReportSourcePreparation(sources, scope, retrieveFile, onReady) {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  const current = useRef({});
  const key = JSON.stringify([scope, sources.map(item => [item.id, item.path])]);
  current.current = { key, sources, retrieveFile, onReady };
  const [preparedKey, setPreparedKey] = useState(null);
  const stop = useCallback(() => {
    controller.current?.abort(); controller.current = null; setBusy(false);
  }, []);
  useEffect(() => {
    stop(); setRows({}); setError(''); setPreparedKey(null);
    return () => controller.current?.abort();
  }, [key, stop]);
  const invalidate = useCallback(() => setPreparedKey(null), []);
  const prepare = useCallback(async () => {
    if (controller.current) return;
    const snapshot = current.current;
    const request = new AbortController(); controller.current = request;
    setBusy(true); setError(''); setPreparedKey(null);
    try {
      const result = await prepareSelectedSources(snapshot.sources, {
        signal: request.signal, retrieveFile: snapshot.retrieveFile,
        onUpdate: (id, row) => {
          if (!request.signal.aborted && current.current.key === snapshot.key)
            setRows(values => ({ ...values, [id]: row }));
        },
      });
      if (!request.signal.aborted && current.current.key === snapshot.key && result.ready) {
        setPreparedKey(snapshot.key); snapshot.onReady?.();
      }
      return result;
    } catch (failure) {
      if (failure.name !== 'AbortError' && current.current.key === snapshot.key) setError(failure.message);
    } finally {
      if (controller.current === request) { controller.current = null; setBusy(false); }
    }
  }, []);
  const summary = selectionSummary(sources, rows);
  return { rows, busy, error, summary, ready: preparedKey === key && summary.ready, prepare, stop, invalidate };
}
